import React, { useMemo, useState, useRef } from "react";
import "./PreviewExam.css";
import { Box, Typography, Card, CardContent, Button } from "@mui/material";
import { BarLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { BaseURL } from "../../../../Hooks/URL";
import PrimaryButton from "../../../../Components/Atoms/Buttons/Primary/PrimaryButton";

function PreviewExam({ submission, loading, isSubmission }) {
  const navigate = useNavigate();
  const questionRefs = useRef([]);

  const questions = useMemo(() => {
    if (!submission) {
      return [];
    }

    if (isSubmission) {
      return submission?.questions || [];
    }

    if (
      submission.task?.type !== "mcq" ||
      !submission.task?.missionTaskMCQ?.questions
    ) {
      return [];
    }
    return submission.task.missionTaskMCQ.questions;
  }, [submission, isSubmission]);

  // Initialize refs array
  React.useEffect(() => {
    questionRefs.current = questionRefs.current.slice(0, questions.length);
  }, [questions.length]);

  const scrollToQuestion = (index) => {
    if (questionRefs.current[index]) {
      questionRefs.current[index].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className="mainContainer">
      {submission && !loading ? (
        <div className="Cont">
          <div className="previewExamContainer">
            <div className="previewExamHeader">
              <div>
                <h2 className="previewExamTitle">
                  {isSubmission ? submission?.mission?.name : submission?.name}
                </h2>
                <p className="previewExamMeta">
                  {questions.length} question
                  {questions.length === 1 ? "" : "s"} • Task type: MCQ
                </p>
                {isSubmission && submission?.team && (
                  <Box sx={{ marginTop: "10px" }}>
                    <Typography variant="body2" sx={{ marginBottom: "5px" }}>
                      <strong>Team Name:</strong>{" "}
                      {submission?.team?.name || "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: "5px" }}>
                      <strong>Team Code:</strong>{" "}
                      {submission?.team?.code || "-"}
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: "5px" }}>
                      <strong>Category:</strong>{" "}
                      {submission?.team?.category?.name || "-"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Age Group:</strong>{" "}
                      {submission?.team?.category?.ageGroup?.name || "-"}
                    </Typography>
                  </Box>
                )}
              </div>

              {!isSubmission ? (
                <PrimaryButton
                  classBtn={"center"}
                  text={"Edit Mission"}
                  style={{
                    textTransform: "capitalize",
                    borderRadius: "5px",
                    color: "var(--text-secondary)",
                    backgroundColor: "var(--primary)",
                    height: "35px",
                    padding: "0 15px",
                    minWidth: "120px",
                    textWrap: "noWrap",
                  }}
                  click={() => navigate(`/missions/${submission?.id}/edit`)}
                />
              ) : (
                ""
              )}
            </div>

            {questions.length === 0 ? (
              <div className="previewExamEmpty">
                <Typography variant="body1">
                  No questions found for this exam.
                </Typography>
              </div>
            ) : (
              <Box
                sx={{
                  marginTop: "10px",
                  width: "100%",
                  display: "flex",
                  gap: "20px",
                  alignItems: "flex-start",
                  flexWrap: { xs: "wrap", md: "nowrap" },
                }}
              >
                {/* Sidebar: Questions index */}
                <Box className="previewSidebar">
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: "10px",
                      fontWeight: "bold",
                      color: "var(--primary)",
                    }}
                  >
                    Questions
                  </Typography>

                  {/* Score Display */}
                  {questions.length > 0 && (
                    <Box
                      sx={{
                        marginBottom: "5px",
                        padding: "10px",
                        backgroundColor: "var(--primary)",
                        borderRadius: "6px",
                        textAlign: "center",
                      }}
                    >
                      {isSubmission ? (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "white",
                              fontWeight: 500,
                              marginBottom: "3px",
                            }}
                          >
                            Total: {submission?.score || 0} /{" "}
                            {submission?.totalScore || 0}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "white",
                              fontSize: "0.75rem",
                              opacity: 0.9,
                            }}
                          >
                            Answered: {submission?.totalAnswerdQuestions || 0} /{" "}
                            {submission?.totalQuestions || 0}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "white",
                              fontWeight: 500,
                              marginBottom: "5px",
                            }}
                          >
                            Total Score
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{
                              color: "white",
                              fontWeight: "bold",
                            }}
                          >
                            {questions.reduce(
                              (total, question) =>
                                total + (parseInt(question.score) || 0),
                              0
                            )}
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}

                  {questions.map((q, index) => (
                    <Button
                      key={q.id ?? index}
                      variant="outlined"
                      fullWidth
                      onClick={() => scrollToQuestion(index)}
                      sx={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                        padding: "8px 12px",
                        marginBottom: "6px",
                        borderColor: "rgba(0,0,0,0.12)",
                        backgroundColor: "transparent",
                        color: "var(--text-primary)",
                        ":hover": {
                          backgroundColor: "rgba(6, 131, 153, 0.05)",
                          borderColor: "var(--primary)",
                        },
                      }}
                    >
                      Q{index + 1}:{" "}
                      <span className="previewSidebarText">
                        {q.text || `Question ${index + 1}`}
                      </span>
                    </Button>
                  ))}

                  <Box className="previewSidebarFooter">
                    <Button
                      size="small"
                      onClick={() => navigate(-1)}
                      sx={{
                        fontSize: "14px",
                        flexBasis: "100%",
                        borderRadius: "5px",
                        textTransform: "none",
                        marginTop: "10px",
                        padding: "8px 0 6px 0",
                        color: "var(--text-secondary)",
                        backgroundColor: "var(--primary)",
                        "&:hover": {
                          backgroundColor: "var(--primary)",
                          opacity: 0.9,
                        },
                      }}
                    >
                      {isSubmission ? "Back" : "Finish preview"}
                    </Button>
                  </Box>
                </Box>

                {/* Main: All Questions */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {questions.map((question, index) => (
                    <Card
                      key={question.id ?? index}
                      ref={(el) => (questionRefs.current[index] = el)}
                      className="previewQuestionCard"
                      sx={{ marginBottom: "20px" }}
                    >
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          className="previewQuestionNumber"
                        >
                          Question {index + 1} of {questions.length}
                        </Typography>

                        {question.imagePath && (
                          <img
                            src={`${BaseURL}/${question?.imagePath}`}
                            alt="Question"
                            style={{
                              maxHeight: "300px",
                              maxWidth: "100%",
                              marginBottom: "10px",
                            }}
                          />
                        )}

                        {question.text && (
                          <Typography
                            variant="h5"
                            className="previewQuestionText"
                          >
                            {question.text}
                          </Typography>
                        )}

                        {typeof question.score !== "undefined" && (
                          <Typography
                            variant="body2"
                            className="previewQuestionScore"
                          >
                            Score: {question.score}
                          </Typography>
                        )}

                        {question.answers && (
                          <Box className="previewAnswersList">
                            {question.answers.map((a, aIndex) => {
                              const isSelected =
                                isSubmission &&
                                question.selectedAnswerId === a.id;
                              const isCorrect = a.isCorrect;

                              return (
                                <div
                                  key={a.id ?? aIndex}
                                  className={`previewAnswerRow ${
                                    isCorrect ? "previewAnswerRow-correct" : ""
                                  } ${
                                    isSelected
                                      ? "previewAnswerRow-selected"
                                      : ""
                                  }`}
                                >
                                  <span className="previewAnswerSymbol">
                                    {a.symbol ||
                                      String.fromCharCode(65 + aIndex)}
                                  </span>
                                  <Box>
                                    {a.imagePath && (
                                      <img
                                        src={`${BaseURL}/${a?.imagePath}`}
                                        alt="Answer"
                                        style={{
                                          maxHeight: "100px",
                                          maxWidth: "100%",
                                        }}
                                      />
                                    )}
                                    {a.text && (
                                      <span
                                        className="previewAnswerText"
                                        style={{
                                          display: "block",
                                          marginTop: a.imagePath ? "10px" : "0",
                                        }}
                                      >
                                        {a.text}
                                      </span>
                                    )}
                                  </Box>
                                  {isCorrect && (
                                    <span
                                      className="previewAnswerBadge"
                                      style={{ margin: "0 0 0 10px" }}
                                    >
                                      Correct
                                    </span>
                                  )}
                                  {isSelected && (
                                    <span
                                      className="previewAnswerBadge"
                                      style={{
                                        margin: "0 0 0 10px",
                                        backgroundColor: "var(--primary)",
                                        color: "var(--text-secondary)",
                                      }}
                                    >
                                      Selected
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}
          </div>
        </div>
      ) : (
        <div className="loadingBox">
          <BarLoader color="var(--primary)" />
        </div>
      )}
    </div>
  );
}

export default PreviewExam;
